import { useState } from "react";
import QRCode from "react-qr-code";
import { Copy, Check, MessageSquare, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// In-app invite UI for any game with a share code. Three ways for the
// host to get the link to a friend:
//
//   1. "Share" — Web Share API. On iOS/Android this pops the native
//      share sheet which already lists Messages, AirDrop, Mail, etc.
//      Hidden on desktop (no native sheet, so we'd just be a stub).
//   2. "Text it" — direct sms: URL scheme. Opens Messages with the
//      link pre-filled. Useful as a one-tap path on mobile even
//      when the user would otherwise have to drill through the
//      share sheet, AND as a hint that SMS is the recommended channel.
//   3. "Copy link" — plain clipboard write. Always works, the only
//      path on desktop. Shows a check mark for 1.5s on success.
//
// The QR code resolves to the same URL — useful for in-person
// sharing ("scan this with your phone") which the share sheet
// can't help with. URL points at /g/<shareCode> (public spectator,
// no login required) so anyone with the link can immediately watch
// the live game.

type Props = {
  shareCode: string;
  // Pretty header text. Caller chooses "Invite to game" vs
  // "Share this game" vs scoped wording.
  title?: string;
  // Optional subtitle below the title. Caller-controlled so the
  // component stays presentation-only.
  subtitle?: string;
};

export function GameInviteCard({
  shareCode,
  title = "Invite to game",
  subtitle,
}: Props) {
  // origin defaults to window.location.origin so this works on
  // localhost dev (http://localhost:5173) AND production
  // (https://cornholio.app) without a config hop.
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://cornholio.app";
  // Auth-gated play URL. If the recipient is on the game's roster
  // they get throw controls; if they're just an org spectator they
  // see the live renderer in read-only mode; if they're not signed
  // in they're routed to sign-in and back. The public /g/<code>
  // page still works for everyone but doesn't show the 3D scene —
  // /dashboard/virtual/play/<code> is the richer landing point.
  const url = `${origin}/dashboard/virtual/play/${shareCode}`;

  // Body text used by both the share sheet and SMS scheme. Short
  // and link-trailing so iMessage previews render the URL big.
  const shareText = `Join my cornhole game — ${url}`;

  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Web Share API availability — only render the "Share" button
  // when the platform can actually do something with it. SSR-safe
  // via the typeof window guard.
  const canShare =
    typeof navigator !== "undefined" &&
    typeof (navigator as { share?: unknown }).share === "function";

  async function handleShare() {
    setError(null);
    try {
      await (navigator as Navigator & { share: (data: ShareData) => Promise<void> }).share({
        title: "Cornholio game",
        text: shareText,
        url,
      });
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch (e) {
      // User cancelled the share sheet — that's not an error worth
      // surfacing. Browsers throw AbortError for cancel; everything
      // else is a real failure we want to know about.
      if ((e as { name?: string }).name === "AbortError") return;
      setError("Couldn't open share sheet.");
    }
  }

  async function handleCopy() {
    setError(null);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Couldn't copy. Long-press the link below instead.");
    }
  }

  function handleSms() {
    // sms: with ?body= works on iOS Safari + Chrome. Android uses
    // sms:?body=… (same syntax with a no-recipient leading colon).
    // window.location triggers the OS handler in both cases.
    setError(null);
    window.location.href = `sms:?&body=${encodeURIComponent(shareText)}`;
  }

  return (
    <div className="rounded-md border border-border bg-muted/10 p-4 space-y-4">
      <div>
        <div className="text-sm font-display uppercase tracking-widest">
          {title}
        </div>
        {subtitle ? (
          <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
        ) : null}
      </div>

      <div className="flex items-start gap-4">
        {/* QR — small enough to share an office desk view, big
            enough to scan from arm's length. White background +
            black foreground for max contrast in any theme. */}
        <div className="rounded bg-white p-2 shrink-0">
          <QRCode value={url} size={120} bgColor="#ffffff" fgColor="#000000" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-xs text-muted-foreground">
            Scan with a phone, or send the link directly. Anyone with the
            link can watch live — no login needed.
          </p>
          <div className="text-[11px] font-mono break-all text-muted-foreground bg-muted/30 rounded px-2 py-1.5">
            {url}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {canShare ? (
          <Button
            onClick={handleShare}
            className="flex-1 gap-1.5"
            variant="default"
          >
            <Share2 size={14} />
            {shared ? "Shared" : "Share"}
          </Button>
        ) : null}
        <Button
          onClick={handleSms}
          className="flex-1 gap-1.5"
          variant={canShare ? "outline" : "default"}
        >
          <MessageSquare size={14} />
          Text it
        </Button>
        <Button
          onClick={handleCopy}
          className="flex-1 gap-1.5"
          variant="outline"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy link"}
        </Button>
      </div>

      {error ? (
        <div className="text-xs text-destructive">{error}</div>
      ) : null}
    </div>
  );
}
